"use client";

import { type ReactNode, useState } from "react";
import {
  CopyButton,
  DataTable,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  LABEL,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  FORMULA_ERROR_STRINGS,
  type FormulaErrorStrings,
  formulaErrorMessage,
  WORKING_BOX,
} from "@/components/tools/chemistry/formula-view";
import { ShareBars } from "@/components/tools/chemistry/share-bars";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type ShareInput,
  sharesOf,
} from "@/lib/tool-kit/chemistry/composition";
import {
  formatFixed,
  formatPlain,
  formatSig,
  type NumberFormatter,
} from "@/lib/tool-kit/chemistry/format";
import {
  molarMass,
  type ParsedFormula,
  parseFormula,
  partMolarMass,
  partToUnicode,
} from "@/lib/tool-kit/chemistry/formula";
import {
  findUnit,
  fromBase,
  MASS,
  toBase,
  unitOptions,
} from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const PERCENT_COMPOSITION_CALCULATOR_STRINGS = {
  modeLabel: "What do you want the percentages of?",
  modeFormula: "The elements in a compound",
  modeMixture: "The components of a mixture or solution",
  formulaLabel: "Chemical formula",
  formulaHint:
    "Case matters: Co is cobalt, CO is carbon monoxide. Hydrates use · or *.",
  formulaPlaceholder: "Fe2O3",
  sampleLabel: "Sample mass (optional)",
  sampleHint: "Fill this in to get the mass of each element in your sample.",
  sampleUnit: "Sample mass unit",
  decimals: "Round percentages to",
  decimalPlaces: "one: {count} decimal place|other: {count} decimal places",
  results: "Results",
  molarMass: "Molar mass",
  sharesHeading: "Mass percent of each element",
  partsHeading: "Mass percent of each part",
  columnElement: "Element",
  columnPart: "Part",
  columnMass: "Mass in one mole (g)",
  columnPercent: "Mass %",
  columnSample: "Mass in the sample",
  total: "Total",
  working: "The working",
  workingLine: "{label}: {mass} ÷ {total} × 100 = {percent}%",
  roundingNote:
    "Rounded percentages can add up to 99.99% or 100.01%; the unrounded shares add up to exactly 100%.",
  rowsHeading: "Components",
  rowName: "Component {number}",
  rowMass: "Mass of component {number}",
  massUnit: "Mass unit",
  addRow: "Add a component",
  removeRow: "Remove component {number}",
  exampleNames: ["Sand", "Salt", "Iron filings"],
  exampleMasses: ["6.20", "3.80", "2.00"],
  mixtureTotal: "Total mass",
  columnComponent: "Component",
  columnMassEntered: "Mass",
  enterRows: "Enter the mass of at least one component to see the percentages.",
  badMass:
    "Each mass must be a number of zero or more. Check the entry for component {number}.",
  allZero: "The total mass is zero, so percentages are undefined.",
  copy: "Copy results",
  copied: "Copied!",
  formulaErrors: FORMULA_ERROR_STRINGS,
  unnamed: "Component {number}",
} as const;

type Strings = typeof PERCENT_COMPOSITION_CALCULATOR_STRINGS;
type Mode = "formula" | "mixture";

const DECIMAL_CHOICES = ["1", "2", "3", "4"] as const;
const MASS_OPTIONS = unitOptions(MASS).filter((o) =>
  ["mg", "g", "kg", "oz", "lb"].includes(o.value)
);
const MAX_ROWS = 12;
const SAMPLE_SIG = 4;

interface Row {
  id: number;
  name: string;
  mass: string;
}

function FormulaPanel({
  formula,
  decimals,
  sample,
  sampleUnit,
}: {
  formula: ParsedFormula;
  decimals: number;
  sample: number | null;
  sampleUnit: string;
}) {
  const strings = useToolUi(PERCENT_COMPOSITION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const mass = molarMass(formula);
  const sampleGrams = sample === null ? null : toBase(sample, MASS, sampleUnit);
  const symbol = findUnit(MASS, sampleUnit).symbol;
  const head = [
    strings.columnElement,
    strings.columnMass,
    strings.columnPercent,
    ...(sampleGrams === null ? [] : [strings.columnSample]),
  ];
  const rows = mass.rows.map((row) => [
    `${row.name} (${row.symbol})`,
    formatPlain(fmt, row.mass),
    `${formatFixed(fmt, row.fraction * 100, decimals)}%`,
    ...(sampleGrams === null
      ? []
      : [
          `${formatSig(
            fmt,
            fromBase(sampleGrams * row.fraction, MASS, sampleUnit),
            SAMPLE_SIG
          )} ${symbol}`,
        ]),
  ]);
  const text = mass.rows
    .map(
      (row) =>
        `${row.symbol}: ${formatFixed(fmt, row.fraction * 100, decimals)}%`
    )
    .join(", ");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.molarMass}
          value={`${formatPlain(fmt, mass.total)} g/mol`}
        />
        {mass.rows.slice(0, 2).map((row) => (
          <StatCard
            key={row.symbol}
            label={row.name}
            value={`${formatFixed(fmt, row.fraction * 100, decimals)}%`}
          />
        ))}
      </StatGrid>
      <div className="space-y-3">
        <h3 className={WIDGET_H3}>{strings.sharesHeading}</h3>
        <ShareBars
          items={mass.rows.map((row) => ({
            label: `${row.name} (${row.symbol})`,
            value: `${formatFixed(fmt, row.fraction * 100, decimals)}%`,
            percent: row.fraction * 100,
          }))}
        />
        <DataTable head={head} rows={rows} />
      </div>
      {formula.parts.length > 1 ? (
        <PartsShares decimals={decimals} formula={formula} total={mass.total} />
      ) : null}
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        {mass.rows.map((row) => (
          <p key={row.symbol}>
            {t(strings.workingLine, {
              label: row.symbol,
              mass: formatPlain(fmt, row.mass),
              total: formatPlain(fmt, mass.total),
              percent: formatFixed(fmt, row.fraction * 100, decimals),
            })}
          </p>
        ))}
        <p className="font-sans text-xs">{strings.roundingNote}</p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={text}
      />
    </>
  );
}

function PartsShares({
  formula,
  total,
  decimals,
}: {
  formula: ParsedFormula;
  total: number;
  decimals: number;
}) {
  const strings = useToolUi(PERCENT_COMPOSITION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const items = formula.parts.map((part) => {
    const share = (partMolarMass(part) * part.coefficient) / total;
    const label =
      part.coefficientText === ""
        ? partToUnicode(part)
        : `${part.coefficientText}${partToUnicode(part)}`;
    return {
      label,
      value: `${formatFixed(fmt, share * 100, decimals)}%`,
      percent: share * 100,
    };
  });
  return (
    <div className="space-y-3">
      <h3 className={WIDGET_H3}>{strings.partsHeading}</h3>
      <ShareBars items={items} />
    </div>
  );
}

function MixturePanel({
  rows,
  unit,
  decimals,
}: {
  rows: readonly Row[];
  unit: string;
  decimals: number;
}) {
  const strings = useToolUi(PERCENT_COMPOSITION_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const symbol = findUnit(MASS, unit).symbol;
  const entered = rows
    .map((row, index) => ({ row, number: index + 1 }))
    .filter(({ row }) => row.mass.trim() !== "");
  if (entered.length === 0) {
    return <Notice>{strings.enterRows}</Notice>;
  }
  const items: ShareInput[] = [];
  for (const { row, number } of entered) {
    const mass = parseMeasuredNumber(row.mass);
    if (mass === null || mass < 0) {
      return (
        <ErrorNotice>{t(strings.badMass, { number: fmt(number) })}</ErrorNotice>
      );
    }
    items.push({
      label:
        row.name.trim() === ""
          ? t(strings.unnamed, { number: fmt(number) })
          : row.name.trim(),
      mass,
    });
  }
  const shares = sharesOf(items);
  if (shares === null) {
    return <ErrorNotice>{strings.allZero}</ErrorNotice>;
  }
  const percentText = (percent: number) =>
    `${formatFixed(fmt, percent, decimals)}%`;
  const text = shares.rows
    .map((row) => `${row.label}: ${percentText(row.percent)}`)
    .join(", ");
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.mixtureTotal}
          value={`${formatPlain(fmt, shares.total)} ${symbol}`}
        />
        {shares.rows.slice(0, 2).map((row) => (
          <StatCard
            key={row.label}
            label={row.label}
            value={percentText(row.percent)}
          />
        ))}
      </StatGrid>
      <ShareBars
        items={shares.rows.map((row) => ({
          label: row.label,
          value: percentText(row.percent),
          percent: row.percent,
        }))}
      />
      <DataTable
        head={[
          strings.columnComponent,
          strings.columnMassEntered,
          strings.columnPercent,
        ]}
        rows={[
          ...shares.rows.map((row) => [
            row.label,
            `${formatPlain(fmt, row.mass)} ${symbol}`,
            percentText(row.percent),
          ]),
          [
            strings.total,
            `${formatPlain(fmt, shares.total)} ${symbol}`,
            "100%",
          ],
        ]}
      />
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        {shares.rows.map((row) => (
          <p key={row.label}>
            {t(strings.workingLine, {
              label: row.label,
              mass: `${formatPlain(fmt, row.mass)} ${symbol}`,
              total: `${formatPlain(fmt, shares.total)} ${symbol}`,
              percent: formatFixed(fmt, row.percent, decimals),
            })}
          </p>
        ))}
        <p className="font-sans text-xs">{strings.roundingNote}</p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={text}
      />
    </>
  );
}

function FormulaProblem({
  messages,
  error,
}: {
  messages: FormulaErrorStrings;
  error: Parameters<typeof formulaErrorMessage>[1];
}) {
  return <ErrorNotice>{formulaErrorMessage(messages, error)}</ErrorNotice>;
}

function RowEditor({
  rows,
  onChange,
  strings,
  fmt,
}: {
  rows: readonly Row[];
  onChange: (rows: Row[]) => void;
  strings: Strings;
  fmt: NumberFormatter;
}) {
  const update = (id: number, patch: Partial<Row>) =>
    onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  return (
    <div className="space-y-3">
      <p className={LABEL}>{strings.rowsHeading}</p>
      {rows.map((row, index) => (
        <div
          className="grid grid-cols-[1fr_1fr_auto] items-end gap-3"
          key={row.id}
        >
          <TextField
            id={`pc-name-${row.id}`}
            label={t(strings.rowName, { number: fmt(index + 1) })}
            onChange={(name) => update(row.id, { name })}
            value={row.name}
          />
          <TextField
            id={`pc-mass-${row.id}`}
            label={t(strings.rowMass, { number: fmt(index + 1) })}
            numeric
            onChange={(mass) => update(row.id, { mass })}
            value={row.mass}
          />
          <button
            aria-label={t(strings.removeRow, { number: fmt(index + 1) })}
            className={BUTTON_SMALL}
            disabled={rows.length <= 2}
            onClick={() => onChange(rows.filter((r) => r.id !== row.id))}
            type="button"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

export default function PercentCompositionCalculatorWidget() {
  const strings = useToolUi(PERCENT_COMPOSITION_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [mode, setMode] = useState<Mode>("formula");
  const [formulaText, setFormulaText] = useState("Fe2O3");
  const [sampleText, setSampleText] = useState("");
  const [unit, setUnit] = useState("g");
  const [decimals, setDecimals] =
    useState<(typeof DECIMAL_CHOICES)[number]>("2");
  const [rows, setRows] = useState<Row[]>(() =>
    strings.exampleNames.map((name, index) => ({
      id: index + 1,
      name,
      mass: strings.exampleMasses[index] ?? "",
    }))
  );
  const [nextId, setNextId] = useState(strings.exampleNames.length + 1);

  const parsed = parseFormula(formulaText);
  const sample =
    sampleText.trim() === "" ? null : parseMeasuredNumber(sampleText);
  const sampleValue = sample !== null && sample >= 0 ? sample : null;

  let body: ReactNode;
  if (mode === "mixture") {
    body = <MixturePanel decimals={Number(decimals)} rows={rows} unit={unit} />;
  } else if (formulaText.trim() === "") {
    body = <Notice>{strings.formulaErrors.empty}</Notice>;
  } else if (parsed.ok) {
    body = (
      <FormulaPanel
        decimals={Number(decimals)}
        formula={parsed.formula}
        sample={sampleValue}
        sampleUnit={unit}
      />
    );
  } else {
    body = (
      <FormulaProblem error={parsed.error} messages={strings.formulaErrors} />
    );
  }

  return (
    <>
      <PillGroup
        label={strings.modeLabel}
        name="pc-mode"
        onChange={setMode}
        options={[
          { value: "formula", label: strings.modeFormula },
          { value: "mixture", label: strings.modeMixture },
        ]}
        value={mode}
      />
      {mode === "formula" ? (
        <TextField
          hint={strings.formulaHint}
          id="pc-formula"
          label={strings.formulaLabel}
          mono
          onChange={setFormulaText}
          placeholder={strings.formulaPlaceholder}
          value={formulaText}
        />
      ) : (
        <>
          <RowEditor
            fmt={fmt}
            onChange={setRows}
            rows={rows}
            strings={strings}
          />
          <button
            className={BUTTON_SECONDARY}
            disabled={rows.length >= MAX_ROWS}
            onClick={() => {
              setRows([...rows, { id: nextId, name: "", mass: "" }]);
              setNextId(nextId + 1);
            }}
            type="button"
          >
            {strings.addRow}
          </button>
        </>
      )}
      <div className="grid gap-4 sm:grid-cols-3">
        {mode === "formula" ? (
          <TextField
            hint={strings.sampleHint}
            id="pc-sample"
            label={strings.sampleLabel}
            numeric
            onChange={setSampleText}
            value={sampleText}
          />
        ) : null}
        <SelectField
          id="pc-unit"
          label={mode === "formula" ? strings.sampleUnit : strings.massUnit}
          onChange={setUnit}
          options={MASS_OPTIONS}
          value={unit}
        />
        <SelectField
          id="pc-decimals"
          label={strings.decimals}
          onChange={setDecimals}
          options={DECIMAL_CHOICES.map((value) => ({
            value,
            label: tPlural(strings.decimalPlaces, locale, Number(value), {
              count: fmt(Number(value)),
            }).trim(),
          }))}
          value={decimals}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {body}
      </section>
    </>
  );
}
